import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader } from "@/components/common";
import ReportFilters, {
  defaultDateRange, formatSec, ReportPagination, REPORT_PAGE_SIZE,
} from "@/components/reports/ReportFilters";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Phone } from "@phosphor-icons/react";
import { formatReportTime } from "@/lib/timezone";

const COLORS = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

export default function CdrPhoneDetail() {
  const { phone } = useParams();
  const { user } = useAuth();
  const accountTz = user?.timezone || "UTC";
  const defaults = defaultDateRange(1, accountTz);
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom);
  const [dateTo, setDateTo] = useState(defaults.dateTo);
  const [applied, setApplied] = useState(defaults);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const tz = data?.timezone || accountTz;

  const load = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/reports/cdr/phone/${encodeURIComponent(phone)}`, {
        params: {
          date_from: applied.dateFrom,
          date_to: applied.dateTo,
          page,
          page_size: REPORT_PAGE_SIZE,
        },
      });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [phone, applied, page]);

  useEffect(() => { load(); }, [load]);

  const rowsTotal = data?.rows_total ?? (data?.items || []).length;

  return (
    <div data-testid="cdr-phone-detail-page">
      <PageHeader
        title={`Call history · ${phone || "—"}`}
        subtitle={`Full CDR history for this number with volume and disposition graphs. Times in ${tz}.`}
      >
        <Button asChild variant="outline" className="rounded-sm">
          <Link to="/cdr" data-testid="cdr-phone-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to CDR
          </Link>
        </Button>
      </PageHeader>

      <ReportFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onApply={() => {
          setPage(1);
          setApplied({ dateFrom, dateTo });
        }}
        onClear={() => {
          const d = defaultDateRange(1, accountTz);
          setDateFrom(d.dateFrom); setDateTo(d.dateTo);
          setPage(1);
          setApplied(d);
        }}
        applying={loading}
        testid="cdr-phone-filters"
        timezone={tz}
      />

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.total_calls === 0 ? (
        <EmptyState
          icon={Phone}
          title="No calls for this number"
          description="Try expanding the date range."
          testid="cdr-phone-empty"
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-4 text-sm mb-4">
            <span><strong>{data.total_calls}</strong> calls</span>
            <span>Talk <strong className="font-mono">{formatSec(data.talk_sec)}</strong></span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-border bg-card p-4 h-72">
              <p className="text-sm font-semibold mb-2">Calls per day</p>
              <ResponsiveContainer width="100%" height="90%">
                <BarChart data={data.by_day || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#4f46e5" name="Calls" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 h-72">
              <p className="text-sm font-semibold mb-2">By disposition</p>
              <ResponsiveContainer width="100%" height="90%">
                <PieChart>
                  <Pie data={data.by_disposition || []} dataKey="count" nameKey="code" outerRadius={90} label>
                    {(data.by_disposition || []).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Start</TableHead>
                  <TableHead>Disposition</TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.items || []).map((r) => (
                  <TableRow key={r.id || r.fs_uuid || `${r.start_time}-${r.phone_number}`}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {formatReportTime(r.start_time, tz, r.start_time_display)}
                    </TableCell>
                    <TableCell className="text-sm">{r.disposition || "—"}</TableCell>
                    <TableCell className="text-sm truncate max-w-[10rem]">
                      {r.campaign_name || r.campaign_id || r.campaign_prefix || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{r.agent_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatSec(r.duration_sec)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ReportPagination
              page={data.page || page}
              pageSize={data.page_size || REPORT_PAGE_SIZE}
              total={rowsTotal}
              onPage={setPage}
              testid="cdr-phone-pagination"
            />
          </div>
        </>
      )}
    </div>
  );
}
